import type { IssueKind, IssuePriority, IssueSize, IssueStatus } from './types';

export type SortBy = 'updated' | 'oldest' | 'priority' | 'pushed' | 'area';

/**
 * WHAT IS BEING HIDDEN FROM THE BACKLOG, AND BY WHICH FIELD — #301.
 *
 * ── EVERY FIELD IS A LIST, AND THAT IS THE CHANGE ───────────────────────────
 *
 * These were scalars: one epic, one size, one area. The mockup Vinnie approved
 * on 30 August 2026 draws a pill reading **"Area · UI or Workflow"**, and the
 * menu behind it puts a tick beside every value that is on. "Show me the two
 * areas I look after" is the ordinary request, and a scalar cannot express it.
 *
 * SO ALL OF THEM ARE LISTS, INCLUDING THE ONES THAT COULD HAVE STAYED SCALAR.
 * Uniformity is doing real work here: the pills, the menu, the counts and the
 * "is anything on" test are each written once and applied to seven fields,
 * rather than seven times with six chances to get one wrong. The old bar's
 * defect was three kinds of control pretending to be one kind; the answer to
 * that is not a fourth kind.
 *
 * ⚠️ AND EMPTY MEANS EVERY VALUE, NEVER NONE. Read the other way — an empty
 * list matching nothing — one untouched filter empties the whole backlog with
 * nothing on screen to say why, which is the exact fault this card was raised
 * about, arriving from inside its own fix.
 */
export interface Filters {
  /**
   * WHICH STATUSES TO SHOW, replacing `hiddenStatuses`, which was the same
   * question asked backwards.
   *
   * Inverted, it could not be drawn: a chip greyed out is easy to miss, there
   * is no honest pill for "not done", and `hidden: []` reads as "nothing is
   * hidden" while meaning "everything is shown". Hiding finished work is now a
   * VIEW's job — see src/views.ts — and this is the ordinary filter it always
   * should have been.
   */
  status: IssueStatus[];
  area: string[];
  /** By phase id. `FILTER_NO_PHASE` for cards in no phase yet — #377, WS-4. */
  phase: string[];
  epic: string[];
  size: IssueSize[];
  priority: IssuePriority[];
  type: IssueKind[];
  /** The Accountable person, by email. `FILTER_NOBODY` for cards nobody owns. */
  assignee: string[];
  /** The doer (Responsible), by email. `FILTER_NOBODY` for cards with no doer. */
  responsible: string[];
  sort: SortBy;
}

/**
 * "Nobody has this" as a filter value.
 *
 * A REAL ANSWER RATHER THAN A GAP — the same rule areas already state, where
 * 244 of this board's cards have none. Unassigned work is precisely what a
 * planning session is looking for. It cannot be an empty string, because an
 * empty LIST already means "everyone".
 */
export const FILTER_NOBODY = '(nobody)';

/**
 * NOTHING FILTERED, which is now genuinely nothing.
 *
 * It used to hide `done` here, and that was the fault in miniature: the board's
 * commonest state was described as "no filters" while three quarters of it was
 * missing. Finished work is hidden by the **Open** view instead, which says so
 * in words on the screen.
 */
export const DEFAULT_FILTERS: Filters = {
  status: [],
  area: [],
  phase: [],
  epic: [],
  size: [],
  priority: [],
  type: [],
  assignee: [],
  responsible: [],
  sort: 'updated',
};

export const SORT_LABEL: Record<SortBy, string> = {
  updated: 'Recently updated',
  oldest: 'Oldest first',
  priority: 'Most urgent',
  // The cards whose target date keeps being pushed later, most-pushed first — the
  // slippage the board should be able to bring to the top. Migration 0125.
  pushed: 'Most pushed',
  // By the part of the product each card belongs to, area name A→Z, so a flat
  // list reads grouped by area without leaving the sort. Cards with no area last.
  area: 'By area',
};

const KEY = 'flightdeck-filters';

/** Only the fields that hide things — everything except `sort`. */
export const FILTER_FIELDS = ['status', 'area', 'phase', 'epic', 'size', 'priority', 'type', 'assignee', 'responsible'] as const;
export type FilterField = (typeof FILTER_FIELDS)[number];

const STATUSES = ['backlog', 'todo', 'inprogress', 'staging', 'inreview', 'done', 'wontdo'];
const SIZES = ['quick', 'medium', 'long'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];
const KINDS = ['task', 'bug', 'improvement', 'feature'];

const list = (v: unknown, allowed?: readonly string[]): string[] => {
  const raw = Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : [];
  const only = raw.filter((x): x is string => typeof x === 'string');
  /* Anything unrecognised is DROPPED rather than kept, so a value written by a
     newer version cannot leave somebody staring at an empty board. Dropping it
     widens what is shown; keeping it would narrow the screen to nothing. */
  return allowed ? only.filter((x) => allowed.includes(x)) : only;
};

/**
 * COERCE ANY STORED OR PARTIAL FILTERS INTO THE CURRENT FULL SHAPE.
 *
 * Two things carry a Filters written by an older version: this browser's
 * localStorage, and every saved view stored on a person's profile. Both may
 * predate a field — `phase` is the newest, #377 — and a Filters missing a field
 * is a live hazard: `passes`, `nothingOn` and `toggle` all read `f.<field>.length`
 * and would throw on `undefined`. So every reader goes through here first, and
 * every field lands as a list (or the sort), never absent.
 *
 * ⚠️ A MISSING FIELD MUST WIDEN THE VIEW, NEVER EMPTY IT. An absent key reads as
 * "all values", the same rule the whole filter bar rests on — read the other way
 * a stored view from before the field would silently hide the whole backlog.
 */
export function normalizeFilters(raw: unknown): Filters {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FILTERS };
  const r = raw as Record<string, unknown>;
  /*
   * ── WHAT SOMEBODY HAD BEFORE #301 ──────────────────────────────────────
   *
   * The old shape: scalars, and `hiddenStatuses` saying what to leave OUT. Read
   * naively, a missing `status` key gives `[]`, which is correct and harmless —
   * but somebody who had deliberately hidden more than `done` would find it all
   * back. So the old key is converted rather than ignored: what was hidden
   * becomes what is shown, the same request in the new grammar.
   */
  const status = Array.isArray(r.status)
    ? list(r.status, STATUSES)
    : Array.isArray(r.hiddenStatuses)
      ? STATUSES.filter((s) => !(r.hiddenStatuses as unknown[]).includes(s))
      : [];
  return {
    /* Every status showing IS "no filter", and saying it the long way would
       put a seven-value pill on screen for somebody who has filtered nothing. */
    status: status.length === STATUSES.length ? [] : (status as IssueStatus[]),
    area: list(r.area),
    phase: list(r.phase),
    epic: list(r.epic),
    size: list(r.size, SIZES) as IssueSize[],
    priority: list(r.priority, PRIORITIES) as IssuePriority[],
    type: list(r.type, KINDS) as IssueKind[],
    assignee: list(r.assignee),
    responsible: list(r.responsible),
    sort: r.sort === 'oldest' || r.sort === 'priority' || r.sort === 'pushed' || r.sort === 'area' ? r.sort : 'updated',
  };
}

/** Kept per-browser so your view of the backlog is yours, not shared. */
export function readFilters(): Filters {
  try {
    return normalizeFilters(JSON.parse(localStorage.getItem(KEY) ?? 'null'));
  } catch {
    return { ...DEFAULT_FILTERS };
  }
}

export function writeFilters(f: Filters): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(f));
  } catch {
    /* private browsing — filters just will not persist */
  }
}

/** Nothing is being hidden by a filter. Sort hides nothing and is not counted. */
export function nothingOn(f: Filters): boolean {
  return FILTER_FIELDS.every((k) => f[k].length === 0);
}

export function isDefault(f: Filters): boolean {
  return nothingOn(f) && f.sort === DEFAULT_FILTERS.sort;
}

/** One value on or off within a field, which is all the menu ever does. */
export function toggle(f: Filters, field: FilterField, value: string): Filters {
  const on = (f[field] as string[]).includes(value);
  const next = on
    ? (f[field] as string[]).filter((v) => v !== value)
    : [...(f[field] as string[]), value];
  return { ...f, [field]: next } as Filters;
}
